/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * Stream Status service
 */

namespace Application.Services {
    'use strict';

    import StreamStatus = Models.StreamStatus;
    import IStreamStatus = Models.IStreamStatus;
    import HalConverter = Models.HalConverter;
    import IHalElement = Models.IHalElement;

    /**
     * StreamStatusService
     */
    export class StreamStatusService {
        public static $name: string = "streamStatusService";
        public static $inject: string[] = ['$interval', '$q', '$http', 'backendService'];

        private static DEFAULT_TRENDING_SECONDS: number = 13;
        private static DEFAULT_TRENDING_MAX_SAMPLES: number = 6;

        private $interval: ng.IIntervalService;
        private qService: ng.IQService;
        private http: ng.IHttpService;
        private backendService: Services.BackendService;
        private backendBaseUrl: string = window.backendUrl;
        private servicePath: string = '';
        private backendUrl: string = this.backendBaseUrl + this.servicePath + 'nodestatus/';
        private streamPipeBackendUrl: string = this.backendUrl + 'streamPipe';
        private blockBackendUrl: string = this.backendBaseUrl + 'flexcollector/devicemap/block';
        private streamPipeTrendingBackendUrl: string = this.backendBaseUrl + 'flexcollector/devicemap/streamPipe';
        private bitrateTrendingStreamPipeQueue: IdMap;
        private paramTrendingBackendUrl: string = this.backendBaseUrl + 'flexcollector/devicemap/'; // switches/1
        private paramDigiconBackendUrl: string = this.backendBaseUrl + 'flexcollector/devicemap/digicon/';

        private paramTrendingBackendUrlSuffix: string = '/trending/'; // port[4].outputBitrate

        constructor($interval: ng.IIntervalService, $q: ng.IQService, $http: ng.IHttpService, backendService: Services.BackendService) {
            this.$interval = $interval;
            this.qService = $q;
            this.http = $http;
            this.backendService = backendService;
            this.bitrateTrendingStreamPipeQueue = {};
            this.initBitrateTrendingInterval();
        }

        public getStreamStatus(elementHref: string, showError: boolean = true): ng.IPromise<IStreamStatus> {
            return this.backendService
                .get(this.streamPipeBackendUrl + '/' + elementHref, false, undefined, false, showError)
                .then((data: ng.IHttpPromiseCallbackArg<IHalElement<StreamStatus>>) => {
                    let halElement: IHalElement<StreamStatus> = data.data;
                    let converted: StreamStatus = HalConverter.convertElement(halElement, StreamStatus.Empty);
                    return converted;
                });
        }

        /* Trending. Move to FlexCollectorService */
        public getStreamPipeTrendingData(elementHref: string,
                                         showError: boolean = true,
                                         endDate: number = undefined): ng.IPromise<Models.IBitRateTrending[]> {
            let endUrl: string = endDate ? '/trending/?endDate=' + endDate : '/trending';
            return this.backendService
                .get(this.streamPipeTrendingBackendUrl + '/' + elementHref + endUrl, false, undefined, false, showError)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    let response: Models.IBitRateTrending[] = data.data.values;
                    return response;
                });
        }

        public getStreamPipeTrendingDataSeconds(elementHref: string,
                                                endDate: number = undefined,
                                                seconds: number): ng.IPromise<Models.ITrendingResult> {

            let endUrl: string = endDate ? '/trending/?endDate=' + endDate + '&seconds=' + seconds
                : '/trending?seconds=' + seconds;
            return this.backendService
                .get(this.streamPipeTrendingBackendUrl + '/' + elementHref + endUrl, false, undefined, false, false)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    return data.data;
                });
        }

        public getStreamPipeTrendingDataSecondsList(elementHref: string,
                                                    endDate: number = undefined,
                                                    seconds: number): ng.IPromise<Models.IBitRateTrending[]> {

            return this.getStreamPipeTrendingDataGroupedDelayed(elementHref, seconds);
        }

        public downloadStreamPipeTrendingData(elementHref: number | string,
                                              endDate: number,
                                              seconds: number): ng.IPromise<void> {
            let endUrl: string = `${this.streamPipeTrendingBackendUrl}/${elementHref}/trending/download` + `?endDate=${endDate}&seconds=${seconds}`;
            return this.http.get(endUrl)
                .then((httpResponse: ng.IHttpPromiseCallbackArg<{}>) => {
                    this.saveFile(httpResponse.data as string, `historical-data-${elementHref}-bitrate.csv`);
                });
        }


        public getBlockTrendingData(blockId: number,
                                    endDate: number = undefined,
                                    seconds?: number): ng.IPromise<Models.IBitRateTrending[]> {
            let query = "";
            if (endDate || seconds) {
                query += "?";
            }
            if (endDate) {
                query += "endDate=" + endDate;
                if (seconds)
                    query+="&"
            }
            if (seconds) {
                query += "seconds=" + seconds;
            }

            let endUrl = '/trending' + query;

            return this.backendService
                .get(this.blockBackendUrl + '/' + blockId + endUrl, false, undefined, false, true)
                .then((data: ng.IHttpPromiseCallbackArg<Models.IBitRateTrending[]>) => {
                    let response: Models.IBitRateTrending[] = data.data;
                    return response;
                });
        }


        public getBlockInputTrendingData(blockId: number,
                                    endDate: number = undefined): ng.IPromise<Models.IBitRateTrending[]> {
            let endUrl: string = endDate ? '/inputTrending/?endDate=' + endDate : '/inputTrending';
            return this.backendService
                .get(this.blockBackendUrl + '/'  + blockId + endUrl, false, undefined, false, true)
                .then((data: ng.IHttpPromiseCallbackArg<Models.IBitRateTrending[]>) => {
                    let response: Models.IBitRateTrending[] = data.data;
                    return response;
                });
        }

        public downloadBlockTrendingData(blockId: number,
                                         endDate?: number,
                                         seconds?: number): ng.IPromise<void> {
            let endUrl: string = this.blockBackendUrl + '/' + blockId + '/trending/download';
            if (endDate) {
                endUrl = endUrl + "?endDate=" + endDate;
                if (seconds) {
                    endUrl = endUrl + "&seconds=" + seconds;
                }
            }
            return this.http.get(endUrl).then((httpResponse: ng.IHttpPromiseCallbackArg<{}>) => {
                this.saveFile(httpResponse.data as string, `historical-data-${blockId}-bitrate.csv`);
            });
        }

        public getParamTrendingData(deviceId: string,
                                    paramName: string,
                                    endDate?: number,
                                    seconds?: number): ng.IPromise<Models.ITrendingResult> {
            let endUrl: string = this.paramTrendingBackendUrl + deviceId + this.paramTrendingBackendUrlSuffix + paramName;
            if (endDate) {
                endUrl = endUrl + "?endDate=" + endDate;
                if (seconds) {
                    endUrl = endUrl + "&seconds=" + seconds;
                }
            }
            return this.backendService
                .get(endUrl, false, undefined, false, true)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    return data.data;
                });
        }

        public getDigiconParamsData(deviceId: string): ng.IPromise<String[]> {
            let endUrl: string = this.paramDigiconBackendUrl + deviceId;
            return this.backendService
                .get(endUrl, false, undefined, false, true)
                .then((data: ng.IHttpPromiseCallbackArg<String>) => {
                    return data.data["params"];
                });
        }

        public downloadParamTrendingData(deviceId: string,
                                         paramName: string,
                                         endDate?: number,
                                         seconds?: number): ng.IPromise<void> {
            let endUrl: string = this.paramTrendingBackendUrl + deviceId + this.paramTrendingBackendUrlSuffix + paramName + '/download';
            if (endDate) {
                endUrl = endUrl + "?endDate=" + endDate;
                if (seconds) {
                    endUrl = endUrl + "&seconds=" + seconds;
                }
            }
            return this.http.get(endUrl).then((httpResponse: ng.IHttpPromiseCallbackArg<{}>) => {
                this.saveFile(httpResponse.data as string, `historical-data-${deviceId}-${paramName}.csv`);
            });
        }

        public saveFile(file: string, filename: string) {
            const blob: Blob = new Blob([file], {
                type: 'text/csv;charset=utf-8;',
            });
            const fileURL: string = window.URL.createObjectURL(blob);
            const downloadLink: ng.IAugmentedJQuery = angular.element(document.getElementsByClassName('download'));
            downloadLink.attr('href', fileURL);
            downloadLink.attr('download', filename);
            downloadLink.attr('target', '_self');
            downloadLink[0].click();
        }

        public getStreamPipeTrendingDataFromSeconds(elementHref: number | string,
                                                    seconds: number,
                                                    maxSamples: number,
                                                    showError: boolean = true): ng.IPromise<Models.IBitRateTrending[]> {
            return this.backendService
                .get(this.streamPipeTrendingBackendUrl + '/' + elementHref +
                    '/trending?seconds=' + seconds + '&maxSamples=' + maxSamples, false, undefined, false, showError)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    let response: Models.IBitRateTrending[] = data.data.values;
                    return response;
                });
        }

        public getBlockTrendingDataFromSeconds(blockId: number | string,
                                               seconds: number,
                                               maxSamples: number,
                                               showError: boolean = true): ng.IPromise<Models.IBitRateTrending[]> {
            return this.backendService
                .get(this.blockBackendUrl + '/' + blockId +
                    '/trending?seconds=' + seconds + '&maxSamples=' + maxSamples, false, undefined, false, showError)
                .then((data: ng.IHttpPromiseCallbackArg<Models.IBitRateTrending[]>) => {
                    let response: Models.IBitRateTrending[] = data.data;
                    return response;
                });
        }
        public getBlockInputTrendingDataFromSeconds(blockId: number | string,
                                               seconds: number,
                                               maxSamples: number,
                                               showError: boolean = true): ng.IPromise<Models.IBitRateTrending[]> {
            return this.backendService
                .get(this.paramTrendingBackendUrl + 'blocks/'  + blockId +
                    '/trending/inputBitrate?seconds=' + seconds + '&maxSamples=' + maxSamples, false, undefined, false, showError)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    let response: Models.IBitRateTrending[] = data.data.values;
                    return response;
                });
        }





        public getNodeTrendingDataFromSeconds(nodeHref: number | string,
                                              seconds: number,
                                              maxSamples: number,
                                              showError: boolean = true): ng.IPromise<Models.ITrendingResult> {
            return this.backendService
                .get(this.paramTrendingBackendUrl + "networkNodes/" + nodeHref +
                    '/trending/bitrate?seconds=' + seconds + '&maxSamples=' + maxSamples, false, undefined, false, showError)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    let response: Models.ITrendingResult = data.data;
                    return response;
                });
        }

        public getStreamPipeTrendingDataFromSecondsAndEnd(elementHref: number | string,
                                                          seconds: number,
                                                          endDate: number,
                                                          showError: boolean = true): ng.IPromise<Models.ITrendingResult> {
            return this.backendService
                .get(this.streamPipeTrendingBackendUrl + '/' + elementHref + '/trending' +
                    '?endDate=' + endDate + '&seconds=' + seconds,
                    false, undefined, false, showError)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    let response: Models.ITrendingResult = data.data;
                    return response;
                });
        }

        public getNodeTrendingDataFromSecondsAndEnd(elementHref: number | string,
                                                    seconds: number,
                                                    endDate: number,
                                                    showError: boolean = true): ng.IPromise<Models.ITrendingResult> {
            return this.backendService
                .get(this.paramTrendingBackendUrl + "networkNodes/" + elementHref + '/trending/bitrate' +
                    '?endDate=' + endDate + '&seconds=' + seconds,
                    false, undefined, false, showError)
                .then((response: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    return response.data;
                });
        }

        public getBlockTrendingDataFromSecondsAndEnd(elementHref: number | string,
                                                     seconds: number,
                                                     endDate: number,
                                                     showError: boolean = true): ng.IPromise<Models.ITrendingResult> {
            return this.backendService
                .get(this.paramTrendingBackendUrl + "blocks/" + elementHref + '/trending/bitrate' +
                    '?endDate=' + endDate + '&seconds=' + seconds,
                    false, undefined, false, showError)
                .then((response: ng.IHttpPromiseCallbackArg<Models.ITrendingResult>) => {
                    return response.data;
                });
        }

        public getStreamPipeTrendingDataGroupedDelayed(elementHref: string, seconds: number): ng.IPromise<Models.IBitRateTrending[]> {
            let id: number = Common.Utils.idFromHref(elementHref);
            let request: BitRateTrendingRequest = this.getOrCreateNewBitRateTrendingRequest(id, seconds, this.bitrateTrendingStreamPipeQueue);
            return request.deferred.promise;
        }

        private getOrCreateNewBitRateTrendingRequest(id: number, seconds: number, idMap: IdMap): BitRateTrendingRequest {
            if (idMap[id] === undefined) {
                idMap[id] = [];
            }
            let requests: BitRateTrendingRequest[] = idMap[id];
            let bitrateTrendingRequest: BitRateTrendingRequest =
                new BitRateTrendingRequest(id, seconds, this.qService.defer());
            requests.push(bitrateTrendingRequest);
            return bitrateTrendingRequest;
        }

        private initBitrateTrendingInterval(): void {
            this.doMakeRequests();
            this.$interval(() => {
                this.doMakeRequests();
            }, 1000);
        }

        private doMakeRequests(): void {
            let tempBitrateTrendingStreamPipeQueue: IdMap = this.bitrateTrendingStreamPipeQueue;
            this.bitrateTrendingStreamPipeQueue = {};
            if (Object.keys(tempBitrateTrendingStreamPipeQueue).length > 0)
                this.getBitrateTrendingListPromise(tempBitrateTrendingStreamPipeQueue,
                    this.paramTrendingBackendUrl + "streamPipe/trending/list");
        }

        private getBitrateTrendingListPromise(requestMap: IdMap, url: string): void {

            let trendings: ITrendingListRequest[] = Object.keys(requestMap).map((id: string) => {
                let seconds: number = 0;
                requestMap[id].forEach((bitrateTrendingRequest: BitRateTrendingRequest) => {
                    if (bitrateTrendingRequest.seconds > seconds) {
                        seconds = bitrateTrendingRequest.seconds;
                    }
                });
                return {
                    id: parseInt(id, 10),
                    seconds: seconds
                } as ITrendingListRequest
            });
            let requests: BitRateTrendingRequest[][] = trendings.map((trendinglistRequest: ITrendingListRequest) => {
                return requestMap[trendinglistRequest.id];
            });
            this.http.post(url, {"trendings": trendings})
                .then((result: ng.IHttpPromiseCallbackArg<Models.IBitRateTrending[][]>) => {
                    let trendings: Models.IBitRateTrending[][] = result.data;
                    trendings.forEach((trending: Models.IBitRateTrending[], index: number) => {
                        let requestsForIndex: BitRateTrendingRequest[] = requests[index];
                        requestsForIndex.forEach((request: BitRateTrendingRequest) => {
                            request.deferred.resolve(trending);
                        });
                    });
                });
        }
    }

    /*
     * We need to store every request even though is for the same id because
     * different UI componentes could be waiting from the same id and we need
     * to notify each one when the promise is resolved
     */
    interface IdMap {
        [key: number]: BitRateTrendingRequest[];
    }

    interface ITrendingListRequest {
        id: number;
        seconds: number
    }

    class BitRateTrendingRequest {
        constructor(public id: number,
                    public seconds: number,
                    public deferred: ng.IDeferred<Models.IBitRateTrending[]>) {
        }
    }
}